import { payTokenControllerInitiate, payTokenControllerPreview } from '@kalba/api-client';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { AlertCircle, Building2, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Price } from '../../components/ui/price';

export const Route = createFileRoute('/pay/$token')({
  component: PublicPayPage,
});

interface PaymentPreview {
  permitReference: string;
  amount: number;
  currency: string;
  entityName: string;
  expiresAt: string;
}

function PublicPayPage() {
  const { token } = Route.useParams();

  const { data, isLoading, error } = useQuery({
    queryKey: ['pay-preview', token],
    queryFn: () => payTokenControllerPreview({ token }) as Promise<PaymentPreview>,
    retry: false,
  });

  const initiateMutation = useMutation({
    mutationFn: () => payTokenControllerInitiate({ token }) as Promise<{ gatewayUrl: string }>,
    onSuccess: (res) => {
      window.location.href = res.gatewayUrl;
    },
  });

  if (isLoading) {
    return (
      <PublicShell>
        <div className="flex justify-center py-10">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
        </div>
      </PublicShell>
    );
  }

  if (error || !data) {
    return (
      <PublicShell>
        <div className="flex flex-col items-center gap-4 py-8 text-center">
          <AlertCircle className="h-10 w-10 text-red-400" />
          <div>
            <h2 className="text-lg font-semibold text-gray-800">Link Expired or Invalid</h2>
            <p className="mt-1 text-sm text-gray-500">
              This payment link has expired or is invalid. Please contact the municipality for a new
              link.
            </p>
          </div>
        </div>
      </PublicShell>
    );
  }

  const expiresAt = new Date(data.expiresAt);
  const hoursLeft = Math.max(0, Math.round((expiresAt.getTime() - Date.now()) / 3600000));

  return (
    <PublicShell>
      <div className="space-y-6">
        {/* Entity + permit header */}
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-50">
            <Building2 className="h-5 w-5 text-brand-600" />
          </div>
          <div>
            <p className="text-sm font-semibold text-gray-900">{data.entityName}</p>
            <p className="text-xs text-gray-500">Permit {data.permitReference}</p>
          </div>
        </div>

        {/* Amount */}
        <div className="rounded-xl bg-gray-50 border border-gray-200 p-5 text-center">
          <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Amount Due</p>
          <div className="text-4xl font-bold text-gray-900">
            <Price value={data.amount} />
          </div>
        </div>

        {/* Expiry warning */}
        <p className="text-center text-xs text-amber-600 font-medium">
          This link expires in {hoursLeft > 0 ? `${hoursLeft} hours` : 'less than an hour'} (
          {expiresAt.toLocaleString()})
        </p>

        {/* Pay button */}
        <Button
          variant="primary"
          className="w-full py-3 text-base"
          disabled={initiateMutation.isPending}
          onClick={() => initiateMutation.mutate()}
        >
          {initiateMutation.isPending ? (
            <span className="flex items-center justify-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Redirecting to payment…
            </span>
          ) : (
            <>
              Pay Now —{' '}
              <Price value={data.amount} className="items-center" iconClassName="size-[0.85em]" />
            </>
          )}
        </Button>

        {initiateMutation.isError && (
          <p className="text-center text-sm text-red-600">
            Something went wrong. Please try again or contact support.
          </p>
        )}
      </div>
    </PublicShell>
  );
}

function PublicShell({ children }: { children: React.ReactNode }) {
  return (
    <main className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
      <div className="w-full max-w-md rounded-2xl bg-white shadow-lg border border-gray-200 overflow-hidden">
        <div className="bg-brand-600 px-6 py-5 flex flex-col items-center gap-3">
          <img
            src="/images/Kalba-Logo-Orange.svg"
            alt="Kalba Municipality"
            className="h-auto w-[120px] object-contain brightness-0 invert"
          />
          <p className="text-brand-100 text-xs">Secure Payment — Powered by Tahseel</p>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </main>
  );
}
